import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { UpstreamConfig } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useDeleteUpstream, useUpstreamCatalog, useUpstreamsList } from '../api/useUpstreams.ts';
import { formatUsdCents } from '../components/formatters.ts';
import { StatusChip } from '../components/StatusChip.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useClipboard } from '../hooks/useClipboard.tsx';
import { useT } from '../i18n/index.ts';

/**
 * `/supply/upstreams` list view. DESIGN.md §6 Data tables — no dividers, zebra
 * via `surface-container` vs `surface-container-low`, dense row padding,
 * `label-md` uppercase column headers.
 *
 * V2 redesign: stats row at top (total, enabled, keys, funding),
 * redesigned columns (name, endpoint, status, keys, mappings, funding,
 * priority, actions). Matches `.stitch/designs/upstreams-list-v2.html`.
 */

function upstreamVariant(row: UpstreamConfig): 'ok' | 'warning' | 'neutral' {
  if (!row.enabled) {
    return 'warning';
  }
  return 'ok';
}

function formatDiagnosticSource(no: number): string {
  return `S${Math.max(0, Math.floor(no)).toString().padStart(3, '0')}`;
}

/* ── Stats row ──────────────────────────────────────────────────────── */

interface StatsRowProps {
  readonly total: number;
  readonly enabled: number;
  readonly accounts: string;
  readonly funding: string;
}

function StatsRow({ total, enabled, accounts, funding }: StatsRowProps): JSX.Element {
  const t = useT();
  return (
    <div
      data-testid="upstreams-stats"
      className="flex gap-8 rounded-xl bg-surface-container-high p-4"
    >
      <div className="flex-1">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-outline">
          {t('upstreams.stats.total')}
        </div>
        <div className="text-[28px] font-bold text-primary [text-shadow:0_0_12px_rgba(173,198,255,0.4)]">
          {total}
        </div>
      </div>
      <div className="w-[1px] bg-outline-variant/20" />
      <div className="flex-1">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-outline">
          {t('upstreams.stats.enabled')}
        </div>
        <div className="text-[28px] font-bold text-primary [text-shadow:0_0_12px_rgba(173,198,255,0.4)]">
          {enabled}
        </div>
      </div>
      <div className="w-[1px] bg-outline-variant/20" />
      <div className="flex-1">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-outline">
          {t('upstreams.stats.accounts')}
        </div>
        <div className="text-[28px] font-bold text-primary [text-shadow:0_0_12px_rgba(173,198,255,0.4)]">
          {accounts}
        </div>
      </div>
      <div className="w-[1px] bg-outline-variant/20" />
      <div className="flex-1">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-outline">
          {t('upstreams.stats.funding')}
        </div>
        <div className="text-[28px] font-bold text-primary [text-shadow:0_0_12px_rgba(173,198,255,0.4)]">
          {funding}
        </div>
      </div>
    </div>
  );
}

/* ── Endpoint copy cell ──────────────────────────────────────────────── */

function EndpointCopyCell({
  upstreamId,
  endpointUrl,
}: {
  readonly upstreamId: string;
  readonly endpointUrl: string;
}): JSX.Element {
  const { copy, copied } = useClipboard();
  const t = useT();
  return (
    <button
      type="button"
      onClick={() => void copy(endpointUrl)}
      title={copied ? t('common.copied') : t('upstreams.row.copy_endpoint')}
      aria-label={t('upstreams.row.copy_endpoint')}
      data-testid={`upstream-row-${upstreamId}-endpoint-copy`}
      className="inline-flex max-w-[240px] items-center gap-2 rounded bg-surface-container-lowest px-1.5 py-0.5 text-left font-mono text-[12px] leading-none text-outline transition-colors hover:bg-surface-container-high hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
    >
      <code className="min-w-0 truncate">{truncateUrl(endpointUrl)}</code>
      <span
        aria-hidden="true"
        className="material-symbols-outlined shrink-0 text-[14px] text-primary"
      >
        {copied ? 'check' : 'content_copy'}
      </span>
    </button>
  );
}

/* ── Table row ──────────────────────────────────────────────────────── */

interface RowProps {
  readonly row: UpstreamConfig;
  readonly modelCount: number;
  readonly zebra: 'odd' | 'even';
  readonly onDelete: (row: UpstreamConfig) => void;
  readonly deleting: boolean;
  readonly statusLabel: string;
}

function UpstreamRow({
  row,
  modelCount,
  zebra,
  onDelete,
  deleting,
  statusLabel,
}: RowProps): JSX.Element {
  const zebraClass = zebra === 'odd' ? '' : 'bg-surface-container-low/30';
  return (
    <tr
      data-testid={`upstream-row-${row.id}`}
      className={`${zebraClass} group transition-colors hover:bg-surface-bright/50`}
    >
      {/* Name */}
      <td className="px-6 py-4 text-sm font-semibold text-primary">
        <Link
          to={`/supply/upstreams/${row.id}`}
          className="hover:underline focus-visible:outline-none focus-visible:underline"
        >
          {row.name}
        </Link>
      </td>

      {/* Endpoint */}
      <td className="px-6 py-4">
        <EndpointCopyCell upstreamId={row.id} endpointUrl={row.endpoint_url} />
      </td>

      {/* Support source */}
      <td className="px-6 py-4 font-mono text-sm font-black tracking-tighter text-primary">
        {formatDiagnosticSource(row.diagnostic_source_no)}
      </td>

      {/* Status */}
      <td className="px-6 py-4">
        <StatusChip
          variant={upstreamVariant(row)}
          label={statusLabel}
          testId={`upstream-row-${row.id}-status`}
        />
      </td>

      {/* Accounts */}
      <td
        className="px-6 py-4 text-sm font-medium text-on-surface"
        data-testid={`upstream-row-${row.id}-accounts`}
      >
        {typeof row.account_count === 'number' ? row.account_count : '—'}
      </td>

      {/* Mappings — model count from catalog */}
      <td
        className="px-6 py-4 text-sm font-medium text-on-surface"
        data-testid={`upstream-row-${row.id}-models`}
      >
        {modelCount}
      </td>

      {/* Funding */}
      <td
        className="px-6 py-4 text-sm font-bold text-primary"
        data-testid={`upstream-row-${row.id}-funding`}
      >
        {typeof row.active_funding_remaining_usd_cents === 'number'
          ? formatUsdCents(row.active_funding_remaining_usd_cents)
          : '—'}
      </td>

      {/* Priority */}
      <td className="px-6 py-4 font-mono text-sm font-black tracking-tighter text-primary">
        {row.priority}
      </td>

      {/* Actions */}
      <td className="px-6 py-4 text-right">
        <div className="flex justify-end gap-1 opacity-40 transition-opacity group-hover:opacity-100">
          <Link
            to={`/supply/upstreams/${row.id}`}
            data-testid={`upstream-row-${row.id}-edit`}
            className="rounded p-1.5 text-on-surface hover:bg-on-surface/10"
          >
            <span className="material-symbols-outlined text-[18px]">edit_note</span>
          </Link>
          <button
            type="button"
            onClick={() => onDelete(row)}
            disabled={deleting}
            data-testid={`upstream-row-${row.id}-delete`}
            className="rounded p-1.5 text-on-surface-variant hover:bg-error/10 hover:text-error disabled:pointer-events-none disabled:opacity-40"
          >
            <span className="material-symbols-outlined text-[18px]">delete</span>
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Truncate a URL to a short display-friendly form.
 * Keeps scheme + first ~18 chars of the host/path, appends "...".
 */
function truncateUrl(url: string): string {
  const maxLen = 22;
  if (url.length <= maxLen) {
    return url;
  }
  return `${url.slice(0, maxLen)}...`;
}

/* ── Delete confirm modal ──────────────────────────────────────────── */

interface DeleteTarget {
  readonly id: string;
  readonly name: string;
}

interface DeleteConfirmProps {
  readonly name: string;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

function DeleteConfirmModal({
  name,
  busy,
  error,
  onCancel,
  onConfirm,
}: DeleteConfirmProps): JSX.Element {
  const t = useT();
  return (
    <div
      role="presentation"
      data-testid="delete-confirm"
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-container-lowest/80 px-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) {
          onCancel();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-confirm-title"
        className="w-full max-w-sm rounded-xl bg-surface-container-highest p-8 text-on-surface"
      >
        <h2 id="delete-confirm-title" className="mb-3 text-base font-semibold">
          {t('upstreams.delete_modal.title')}
        </h2>
        <p className="mb-6 text-sm text-outline">
          <span className="font-semibold text-on-surface">{name}</span>
          {t('upstreams.delete_modal.body_suffix')}
        </p>
        {error !== null ? (
          <p className="mb-4 text-xs text-error" role="alert" data-testid="delete-confirm-error">
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-highest disabled:pointer-events-none disabled:opacity-40"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="delete-confirm-submit"
            className="inline-flex items-center justify-center rounded-lg bg-error-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-error-container transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-highest disabled:pointer-events-none disabled:opacity-40"
          >
            {busy ? t('common.deleting') : t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Page component ─────────────────────────────────────────────────── */

export function UpstreamsList(): JSX.Element {
  const list = useUpstreamsList();
  const catalog = useUpstreamCatalog();
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteMutationId, setDeleteMutationId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMutation = useDeleteUpstream(deleteMutationId ?? '');

  const modelCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const entries = catalog.data?.entries ?? [];
    for (const entry of entries) {
      counts.set(entry.upstream.id, entry.models.length);
    }
    return counts;
  }, [catalog.data]);

  const filtered = useMemo(() => {
    const rows = list.data ?? [];
    const query = search.trim().toLowerCase();
    if (query.length === 0) {
      return rows;
    }
    return rows.filter(
      (row) =>
        row.name.toLowerCase().includes(query) ||
        row.endpoint_url.toLowerCase().includes(query) ||
        row.kind.toLowerCase().includes(query),
    );
  }, [list.data, search]);

  /** Compute stats from the full (unfiltered) list. */
  const stats = useMemo(() => {
    const rows = list.data ?? [];
    const total = rows.length;
    const enabled = rows.filter((r) => r.enabled).length;
    const hasAccountSummary = rows.every((r) => typeof r.account_count === 'number');
    const hasFundingSummary = rows.every(
      (r) => typeof r.active_funding_remaining_usd_cents === 'number',
    );
    const accounts = hasAccountSummary
      ? rows.reduce((sum, row) => sum + (row.account_count ?? 0), 0).toString()
      : '—';
    const activeFundingCents = hasFundingSummary
      ? rows.reduce((sum, row) => sum + (row.active_funding_remaining_usd_cents ?? 0), 0)
      : null;
    return {
      total,
      enabled,
      accounts,
      funding: activeFundingCents === null ? '—' : formatUsdCents(activeFundingCents),
    };
  }, [list.data]);

  async function confirmDelete(): Promise<void> {
    if (deleteTarget === null) {
      return;
    }
    setDeleteMutationId(deleteTarget.id);
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync();
      setDeleteTarget(null);
      setDeleteMutationId(null);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setDeleteError(message);
      toast.error(message);
    }
  }

  return (
    <section data-testid="upstreams-list-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-2">
          <h2 className="text-[28px] font-semibold leading-tight text-on-surface">
            {t('upstreams.title')}
          </h2>
          <p className="text-sm font-medium text-outline">{t('upstreams.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex h-10 items-center gap-2 rounded-lg bg-surface-container-lowest px-3 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-surface">
            <span
              aria-hidden="true"
              className="material-symbols-outlined text-[16px] text-outline"
            >
              search
            </span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder={t('upstreams.search.placeholder')}
              aria-label={t('upstreams.search.aria_label')}
              data-testid="upstreams-search"
              className="w-56 bg-transparent text-[13px] text-on-surface placeholder:text-outline focus:outline-none"
            />
          </label>
          <button
            type="button"
            data-testid="upstreams-new"
            onClick={() => navigate('/supply/upstreams/new')}
            title={t('upstreams.new.title')}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-container px-4 py-2 text-sm font-bold text-on-primary shadow-lg shadow-primary-container/10 transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:pointer-events-none disabled:opacity-40"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-sm">
              add
            </span>
            {t('upstreams.new')}
          </button>
        </div>
      </header>

      {list.isError ? (
        <div
          role="alert"
          data-testid="upstreams-error"
          className="flex items-center justify-between gap-4 rounded-xl bg-error-container px-6 py-4 text-on-error-container"
        >
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">{t('upstreams.error.title')}</span>
            <span className="text-xs">
              {list.error instanceof OpsApiError
                ? `${list.error.code}: ${list.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => void list.refetch()}
            data-testid="upstreams-retry"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-error-container"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : null}

      {list.isPending ? (
        <div
          data-testid="upstreams-loading"
          className="flex flex-col gap-2 rounded-xl bg-surface-container p-4"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg bg-surface-container-low"
              aria-hidden="true"
            />
          ))}
          <span className="sr-only">{t('upstreams.loading_aria')}</span>
        </div>
      ) : null}

      {list.isSuccess && filtered.length === 0 ? (
        <div
          data-testid="upstreams-empty"
          className="flex flex-col items-center gap-4 rounded-xl bg-surface-container px-8 py-16 text-center"
        >
          <h3 className="text-2xl font-semibold tracking-tight text-on-surface">
            {t('upstreams.empty.title')}
          </h3>
          <p className="max-w-md text-sm text-outline">
            {search.length > 0
              ? t('upstreams.empty.search_body')
              : t('upstreams.empty.initial_body')}
          </p>
        </div>
      ) : null}

      {list.isSuccess && filtered.length > 0 ? (
        <>
          {/* Stats row */}
          <StatsRow
            total={stats.total}
            enabled={stats.enabled}
            accounts={stats.accounts}
            funding={stats.funding}
          />

          {/* Data table */}
          <div className="overflow-hidden rounded-xl border border-outline-variant/10 bg-surface-container shadow-2xl">
            <table data-testid="upstreams-table" className="w-full border-collapse text-left">
              <thead className="bg-surface-container-high">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant"
                  >
                    {t('upstreams.column.name')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant"
                  >
                    {t('upstreams.column.endpoint')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant"
                  >
                    {t('upstreams.column.source')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant"
                  >
                    {t('upstreams.column.status')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant"
                  >
                    {t('upstreams.column.accounts')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant"
                  >
                    {t('upstreams.column.mappings')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant"
                  >
                    {t('upstreams.column.funding')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant"
                  >
                    {t('upstreams.column.priority')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant"
                  >
                    {t('upstreams.column.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/5">
                {filtered.map((row, i) => (
                  <UpstreamRow
                    key={row.id}
                    row={row}
                    modelCount={modelCounts.get(row.id) ?? 0}
                    zebra={i % 2 === 0 ? 'odd' : 'even'}
                    onDelete={(target) => {
                      setDeleteError(null);
                      setDeleteTarget({ id: target.id, name: target.name });
                    }}
                    deleting={deleteMutationId === row.id && deleteMutation.isPending}
                    statusLabel={
                      row.enabled ? t('upstreams.status.enabled') : t('upstreams.status.disabled')
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {deleteTarget !== null ? (
        <DeleteConfirmModal
          name={deleteTarget.name}
          busy={deleteMutation.isPending}
          error={deleteError}
          onCancel={() => {
            if (!deleteMutation.isPending) {
              setDeleteTarget(null);
              setDeleteMutationId(null);
              setDeleteError(null);
            }
          }}
          onConfirm={() => void confirmDelete()}
        />
      ) : null}
    </section>
  );
}
